// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n, getCurrencySymbol } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import FieldGroup from 'shared/components/form/FieldGroup';
import Page from 'main-app/components/Page';
import ReceivePurchaseOrderMutation from 'main-app/mutations/ReceivePurchaseOrder';
import BarcodeScannerInput from 'shared/components/common/BarcodeScannerInput';
import * as Actions from 'main-app/store/Actions';
import moment from 'moment';

type Props = {
  history: RouterHistory,
  match: {
    params: {
      id: string,
    },
  },
};

type State = {
  receivingQuantities: { [string]: number },
  validationErrors: { [string]: string },
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 1200px;
`;

const BackButton = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 16px;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const HeaderSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`;

const PONumber = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #1a1a1a;
`;

const ProgressSection = styled.div`
  margin-bottom: 24px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 32px;
  background-color: #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #10b981;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const ProgressLabel = styled.div`
  margin-top: 8px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ValidationError = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
`;

const LotInfoBanner = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1e40af;
`;

class ReceivingInterface extends PureComponent<Props, State> {
  state = {
    receivingQuantities: {},
    validationErrors: {},
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Initialize receiving quantities when data loads
    if (Object.keys(this.state.receivingQuantities).length === 0 && this.queryData) {
      const po = this.queryData.purchaseOrder;
      if (po && po.lineItems) {
        const initialQuantities = {};
        po.lineItems.forEach(line => {
          const remaining = line.quantity - (line.quantityReceived || 0);
          initialQuantities[line.id] = remaining;
        });
        this.setState({ receivingQuantities: initialQuantities });
      }
    }
  }

  queryData: ?Object = null;

  handleQuantityChange = (lineId: string, value: number) => {
    this.setState(prevState => ({
      receivingQuantities: {
        ...prevState.receivingQuantities,
        [lineId]: value,
      },
      validationErrors: {
        ...prevState.validationErrors,
        [lineId]: '',
      },
    }));
  };

  validateReceiving = (lineItems: Array<*>) => {
    const { receivingQuantities } = this.state;
    const errors = {};
    let hasErrors = false;

    lineItems.forEach(line => {
      const receivingQty = receivingQuantities[line.id] || 0;
      const remaining = line.quantity - (line.quantityReceived || 0);

      if (receivingQty > remaining) {
        errors[line.id] = i18n.t('Cannot exceed remaining quantity');
        hasErrors = true;
      }

      if (receivingQty < 0) {
        errors[line.id] = i18n.t('Quantity must be positive');
        hasErrors = true;
      }
    });

    const totalReceiving = Object.values(receivingQuantities).reduce((sum, qty) => sum + qty, 0);
    if (totalReceiving === 0) {
      Actions.alertNotification(
        i18n.t('Please enter at least one quantity to receive'),
        'warning'
      );
      return false;
    }

    if (hasErrors) {
      this.setState({ validationErrors: errors });
      return false;
    }

    return true;
  };

  handleCompleteReceipt = async () => {
    const po = this.queryData?.purchaseOrder;
    if (!po) return;

    // Fill remaining quantities for all lines
    const updatedQuantities = {};
    po.lineItems.forEach(line => {
      const remaining = line.quantity - (line.quantityReceived || 0);
      updatedQuantities[line.id] = remaining;
    });

    const receivedItems = po.lineItems
      .filter(line => {
        const remaining = line.quantity - (line.quantityReceived || 0);
        return remaining > 0;
      })
      .map(line => ({
        purchaseOrderLineId: line.id,
        itemId: line.item.id,
        quantityReceived: line.quantity - (line.quantityReceived || 0),
      }));

    if (receivedItems.length === 0) {
      Actions.alertNotification(
        i18n.t('All items have already been fully received'),
        'warning'
      );
      return;
    }

    Actions.alertNotification(
      i18n.t('Processing complete receipt...'),
      'info'
    );

    try {
      const response = await ReceivePurchaseOrderMutation.commit({
        variables: {
          input: {
            purchaseOrderId: po.id,
            receivedItems,
            notes: null,
          },
        },
      });

      if (response.receivePurchaseOrder?.success) {
        Actions.alertNotification(
          i18n.t('All remaining items received successfully. PO status: ') + response.receivePurchaseOrder.poStatus,
          'success'
        );

        setTimeout(() => {
          this.props.history.push(`/purchase-order/${po.id}`);
        }, 1500);
      } else {
        Actions.alertNotification(
          response.receivePurchaseOrder?.message || i18n.t('Failed to receive items'),
          'error'
        );
      }
    } catch (e) {
      Actions.alertNotification(
        i18n.t(`Failed to receive items: ${e.message}`),
        'error'
      );
    }
  };

  handlePartialReceive = async () => {
    const po = this.queryData?.purchaseOrder;
    if (!po) return;

    if (!this.validateReceiving(po.lineItems)) {
      return;
    }

    Actions.alertNotification(
      i18n.t('Processing receipt...'),
      'info'
    );

    const { receivingQuantities } = this.state;

    const receivedItems = po.lineItems
      .filter(line => receivingQuantities[line.id] > 0)
      .map(line => ({
        purchaseOrderLineId: line.id,
        itemId: line.item.id,
        quantityReceived: receivingQuantities[line.id],
      }));

    try {
      const response = await ReceivePurchaseOrderMutation.commit({
        variables: {
          input: {
            purchaseOrderId: po.id,
            receivedItems,
            notes: null,
          },
        },
      });

      if (response.receivePurchaseOrder?.success) {
        const itemCount = receivedItems.length;
        const newStatus = response.receivePurchaseOrder.poStatus;
        
        Actions.alertNotification(
          i18n.t(`Received ${itemCount} item${itemCount > 1 ? 's' : ''}. PO status: ${newStatus}`),
          'success'
        );

        setTimeout(() => {
          this.props.history.push(`/purchase-order/${po.id}`);
        }, 1500);
      } else {
        Actions.alertNotification(
          response.receivePurchaseOrder?.message || i18n.t('Failed to receive items'),
          'error'
        );
      }
    } catch (e) {
      Actions.alertNotification(
        i18n.t(`Failed to receive items: ${e.message}`),
        'error'
      );
    }
  };

  handleCancel = () => {
    const po = this.queryData?.purchaseOrder;
    if (po) {
      this.props.history.push(`/purchase-order/${po.id}`);
    }
  };

  handleBarcodeScan = (result: Object) => {
    const po = this.queryData?.purchaseOrder;
    if (!po || result.type !== 'ITEM') return;

    // Find matching PO line item
    const matchingLine = po.lineItems.find(
      line =>
        line.item.itemNumber === result.code || line.item.id === result.id,
    );

    if (matchingLine) {
      const remaining = matchingLine.quantity - (matchingLine.quantityReceived || 0);
      const currentQty = this.state.receivingQuantities[matchingLine.id] || 0;
      const newQty = Math.min(currentQty + 1, remaining);

      this.setState(prev => ({
        receivingQuantities: {
          ...prev.receivingQuantities,
          [matchingLine.id]: newQty,
        },
      }));

      Actions.alertNotification(
        i18n.t('Scanned: {{name}} (receiving {{qty}})', {
          name: matchingLine.item.name,
          qty: newQty,
        }),
        'success',
      );
    } else {
      Actions.alertNotification(
        i18n.t('Item "{{code}}" not found in this purchase order.', {
          code: result.code,
        }),
        'warning',
      );
    }
  };

  formatCurrency = (amount: ?number) => {
    if (!amount) return `${getCurrencySymbol()}0.00`;
    return `${getCurrencySymbol()}${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  formatDate = (date: ?string) => {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY');
  };

  render() {
    const { match, history } = this.props;
    const { id } = match.params;
    const { receivingQuantities, validationErrors } = this.state;

    return (
      <Page title={i18n.t('Receive Purchase Order')} history={history}>
        <QueryRenderer
          environment={relayEnvironment}
          dataFrom="STORE_THEN_NETWORK"
          query={graphql`
            query ReceivingInterfaceQuery($id: ID!) {
              purchaseOrder(id: $id) {
                id
                poNumber
                vendor {
                  id
                  vendorNumber
                  name
                  email
                  phone
                }
                status
                totalAmount
                expectedDeliveryDate
                lineItems {
                  id
                  item {
                    id
                    itemNumber
                    name
                    lotTracking
                  }
                  quantity
                  quantityReceived
                  unitPrice
                }
              }
            }
          `}
          variables={{ id }}
          render={({ error, props }) => {
            if (error) {
              return <div>Error: {error.message}</div>;
            }

            if (!props) {
              return <Loader />;
            }

            const po = props.purchaseOrder;
            if (!po) {
              return <div>Purchase order not found</div>;
            }

            this.queryData = props;

            const totalOrdered = po.lineItems.reduce((sum, line) => sum + line.quantity, 0);
            const totalReceived = po.lineItems.reduce((sum, line) => sum + (line.quantityReceived || 0), 0);
            const percentComplete = totalOrdered > 0 ? (totalReceived / totalOrdered) * 100 : 0;

            const columns = [
              {
                id: 'itemNumber',
                Header: i18n.t('Item Number'),
                accessor: (row) => row.item?.itemNumber || '-',
                width: 150,
              },
              {
                id: 'itemName',
                Header: i18n.t('Item Name'),
                accessor: (row) => row.item?.name || '-',
                width: 200,
              },
              {
                id: 'ordered',
                Header: i18n.t('Ordered Qty'),
                accessor: 'quantity',
                width: 120,
              },
              {
                id: 'received',
                Header: i18n.t('Already Received'),
                accessor: 'quantityReceived',
                width: 150,
                Cell: ({ value }) => value || 0,
              },
              {
                id: 'remaining',
                Header: i18n.t('Remaining'),
                accessor: (row) => row.quantity - (row.quantityReceived || 0),
                width: 120,
              },
              {
                id: 'receivingNow',
                Header: i18n.t('Receiving Now'),
                accessor: () => null,
                width: 150,
                Cell: ({ original }) => (
                  <div>
                    <FieldGroup
                      rawField
                      type="number"
                      value={receivingQuantities[original.id] || 0}
                      onChange={(value) => this.handleQuantityChange(original.id, parseFloat(value) || 0)}
                      min={0}
                      step={0.01}
                    />
                    {validationErrors[original.id] && (
                      <ValidationError>{validationErrors[original.id]}</ValidationError>
                    )}
                  </div>
                ),
              },
              {
                id: 'lotNumber',
                Header: i18n.t('Lot Tracking'),
                accessor: () => null,
                width: 150,
                Cell: ({ original }) => {
                  if (!original.item?.lotTracking) {
                    return <span style={{ color: '#999' }}>-</span>;
                  }
                  return (
                    <span style={{ color: '#059669', fontWeight: 600 }}>
                      {i18n.t('Auto')}
                    </span>
                  );
                },
              },
            ];

            return (
              <Wrapper>
                <BackButton onClick={this.handleCancel}>
                  <Icon type="circle-arrow-left" size={20} />
                </BackButton>

                <HeaderSection>
                  <PONumber>{i18n.t('Receive Purchase Order')}: {po.poNumber}</PONumber>
                  
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>{i18n.t('Vendor')}</InfoLabel>
                      <InfoValue>{po.vendor?.name || '-'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>{i18n.t('Expected Delivery')}</InfoLabel>
                      <InfoValue>{this.formatDate(po.expectedDeliveryDate)}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>{i18n.t('Total Amount')}</InfoLabel>
                      <InfoValue>{this.formatCurrency(po.totalAmount)}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>{i18n.t('Vendor Email')}</InfoLabel>
                      <InfoValue>{po.vendor?.email || '-'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>{i18n.t('Vendor Phone')}</InfoLabel>
                      <InfoValue>{po.vendor?.phone || '-'}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>{i18n.t('Status')}</InfoLabel>
                      <InfoValue style={{ textTransform: 'uppercase' }}>{po.status}</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </HeaderSection>

                <ProgressSection>
                  <ProgressBar>
                    <ProgressFill style={{ width: `${percentComplete}%` }}>
                      {percentComplete > 0 && `${percentComplete.toFixed(0)}%`}
                    </ProgressFill>
                  </ProgressBar>
                  <ProgressLabel>
                    {i18n.t('Overall Progress')}: {totalReceived.toFixed(2)} / {totalOrdered.toFixed(2)} {i18n.t('items received')}
                  </ProgressLabel>
                </ProgressSection>

                {po.lineItems.some(line => line.item?.lotTracking) && (
                  <LotInfoBanner>
                    <Icon type="circle-info" size={16} />
                    {i18n.t('Lot numbers will be automatically generated for lot-tracked items upon receiving.')}
                  </LotInfoBanner>
                )}

                <BarcodeScannerInput
                  onScan={this.handleBarcodeScan}
                  onScanNotFound={code =>
                    Actions.alertNotification(
                      i18n.t('Barcode "{{code}}" not found.', { code }),
                      'warning',
                    )
                  }
                  placeholder={i18n.t('Scan item barcode to increment receiving quantity...')}
                  filterType="ITEM"
                />

                <Table
                  columns={columns}
                  data={po.lineItems}
                />

                <ActionButtons>
                  <Button
                    variant="secondary"
                    onClick={this.handleCancel}
                  >
                    {i18n.t('Cancel')}
                  </Button>
                  <Button
                    variant="default"
                    onClick={this.handleCompleteReceipt}
                  >
                    {i18n.t('Complete Receipt')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={this.handlePartialReceive}
                  >
                    {i18n.t('Partial Receive')}
                  </Button>
                </ActionButtons>
              </Wrapper>
            );
          }}
        />
      </Page>
    );
  }
}

export default ReceivingInterface;
